/**
 * Google Maps opens an internal InfoWindow (logAsInternal) when a basemap POI is clicked.
 * There is no public API to close it; capture the instance and call close() on the next interaction.
 */

type InfoWindowWithInternalFlag = google.maps.InfoWindow & { logAsInternal?: boolean };

let activePoiInfoWindow: InfoWindowWithInternalFlag | null = null;
let originalSetPosition: typeof google.maps.InfoWindow.prototype.setPosition | null = null;
let captureRefCount = 0;

function clickCloseButtonsInNode(root: ParentNode): void {
    root.querySelectorAll("button[aria-label]").forEach((btn) => {
        const label = btn.getAttribute("aria-label")?.toLowerCase() ?? "";
        if (label === "close" || label.startsWith("close ")) {
            (btn as HTMLButtonElement).click();
        }
    });

    root.querySelectorAll("*").forEach((el) => {
        if (el instanceof Element && el.shadowRoot) {
            clickCloseButtonsInNode(el.shadowRoot);
        }
    });
}

function installCapture(): void {
    if (originalSetPosition != null) return;

    originalSetPosition = google.maps.InfoWindow.prototype.setPosition;
    google.maps.InfoWindow.prototype.setPosition = function (
        this: InfoWindowWithInternalFlag,
        ...args: Parameters<typeof google.maps.InfoWindow.prototype.setPosition>
    ) {
        if (this.logAsInternal) {
            activePoiInfoWindow = this;
        }
        return originalSetPosition!.apply(this, args);
    };
}

function uninstallCapture(): void {
    if (originalSetPosition == null) return;

    google.maps.InfoWindow.prototype.setPosition = originalSetPosition;
    originalSetPosition = null;
    activePoiInfoWindow = null;
}

/** Reference-counted patch; safe with multiple map instances. */
export function retainNativePoiInfoWindowCapture(): () => void {
    captureRefCount += 1;
    if (captureRefCount === 1) {
        installCapture();
    }

    return () => {
        captureRefCount -= 1;
        if (captureRefCount <= 0) {
            captureRefCount = 0;
            uninstallCapture();
        }
    };
}

/** Closes Google's default POI place card / overview if one is open. */
export function dismissNativePoiInfoWindow(map: google.maps.Map | null): void {
    try {
        activePoiInfoWindow?.close();
    } catch {
        // Ignore if the map already tore down the window.
    }
    activePoiInfoWindow = null;

    if (map) {
        // Clears Google's internal POI selection state on vector basemaps.
        map.setOptions({ clickableIcons: false });
        map.setOptions({ clickableIcons: true });
    }

    const div = map?.getDiv();
    if (!div) return;

    clickCloseButtonsInNode(div);

    div.querySelectorAll(".gm-ui-hover-effect").forEach((el) => {
        (el as HTMLElement).click();
    });

    const active = document.activeElement;
    if (active instanceof HTMLElement && div.contains(active)) {
        active.blur();
    }
}
